import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, MessageCircle, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { createLead } from "@/lib/api";
import { waLink, CONTACT } from "@/data/site";

const emptyForm = (defaultType, presetService) => ({
  name: "",
  email: "",
  phone: "",
  request_type: defaultType || "general",
  service: presetService || "",
  location: "",
  preferred_date: "",
  language: "",
  preferred_contact: "whatsapp",
  message: "",
  consent: false,
});

/**
 * MedicalSupportForm — captures a support request and posts to /api/leads.
 * Backend triggers admin notification + customer confirmation emails.
 * `presetService` prepopulates the service field on service/care/location pages.
 */
export const ContactForm = ({ defaultType = "general", presetService = "", source = "website", onDone }) => {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState(emptyForm(defaultType, presetService));
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const REQUEST_TYPES = [
    { value: "medical", label: t("form.typeMedical") },
    { value: "nursing", label: t("form.typeNursing") },
    { value: "general", label: t("form.typeGeneral") },
  ];

  const PREF_CONTACT = [
    { value: "whatsapp", label: t("form.contactWhatsapp") },
    { value: "email", label: t("form.contactEmail") },
    { value: "phone", label: t("form.contactPhone") },
  ];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error(t("form.errName"));
    if (!form.email.trim() && !form.phone.trim()) return toast.error(t("form.errContact"));
    if (!form.consent) return toast.error(t("form.errConsent"));

    setLoading(true);
    try {
      await createLead({ ...form, source, language: form.language || i18n.language });
      setDone(true);
      toast.success(t("form.successToast"));
      onDone?.();
    } catch (err) {
      toast.error(err.message || t("form.errContact"));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    const typeLabel = REQUEST_TYPES.find((r) => r.value === form.request_type)?.label || "support";
    const svc = form.service ? ` about ${form.service}` : "";
    const loc = form.location ? ` in ${form.location}` : "";
    const waText = `Hello Doctors Vietnam, I just submitted a ${typeLabel} request${svc}${loc}. My name is ${form.name}.`;
    return (
      <div data-testid="lead-success" className="flex flex-col items-center gap-4 py-6 text-center">
        <span className="grid h-14 w-14 place-items-center rounded-full bg-mint text-brand">
          <CheckCircle2 className="h-7 w-7" />
        </span>
        <div>
          <h3 className="text-xl font-bold text-[hsl(var(--vc-ink))]">{t("form.successTitle", { name: form.name.split(" ")[0] })}</h3>
          <p className="mt-1 text-sm text-[hsl(var(--vc-slate))]">{t("form.successText")}</p>
        </div>
        <Button asChild className="w-full bg-[#25D366] text-white hover:bg-[#20bd59]" data-testid="success-whatsapp-btn">
          <a href={waLink(waText)} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="mr-2 h-4 w-4" /> {t("form.continueWhatsapp")}
          </a>
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("form.name")} required>
          <Input data-testid="input-name" placeholder={t("form.namePlaceholder")} value={form.name} onChange={set("name")} />
        </Field>
        <Field label={t("form.requestType")}>
          <Select value={form.request_type} onValueChange={set("request_type")}>
            <SelectTrigger data-testid="select-request-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {REQUEST_TYPES.map((rt) => (
                <SelectItem key={rt.value} value={rt.value} data-testid={`request-type-${rt.value}`}>{rt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("form.email")}>
          <Input data-testid="input-email" type="email" placeholder="you@email.com" value={form.email} onChange={set("email")} />
        </Field>
        <Field label={t("form.phone")}>
          <Input data-testid="input-phone" placeholder={CONTACT.whatsappDisplay} value={form.phone} onChange={set("phone")} />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("form.service")}>
          <Input data-testid="input-service" placeholder={t("form.servicePlaceholder")} value={form.service} onChange={set("service")} />
        </Field>
        <Field label={t("form.location")}>
          <Input data-testid="input-location" placeholder={t("form.locationPlaceholder")} value={form.location} onChange={set("location")} />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("form.preferredDate")}>
          <Input data-testid="input-preferred-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} />
        </Field>
        <Field label={t("form.preferredContact")}>
          <Select value={form.preferred_contact} onValueChange={set("preferred_contact")}>
            <SelectTrigger data-testid="select-preferred-contact"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PREF_CONTACT.map((pc) => (
                <SelectItem key={pc.value} value={pc.value} data-testid={`preferred-contact-${pc.value}`}>{pc.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label={t("form.message")}>
        <Textarea data-testid="input-message" rows={4} placeholder={t("form.messagePlaceholder")} value={form.message} onChange={set("message")} />
      </Field>

      <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-[hsl(var(--vc-line))] bg-[hsl(var(--vc-mist))] p-3 text-sm text-[hsl(var(--vc-slate))]">
        <Checkbox
          data-testid="checkbox-consent"
          checked={form.consent}
          onCheckedChange={(v) => setForm((f) => ({ ...f, consent: !!v }))}
          className="mt-0.5"
        />
        <span>{t("form.consentLabel")}</span>
      </label>

      <Button type="submit" disabled={loading} data-testid="submit-lead-btn"
        className="w-full bg-brand py-6 text-base font-bold text-white hover:opacity-90">
        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
        {loading ? t("form.sending") : t("form.send")}
      </Button>
      <p className="text-center text-xs text-[hsl(var(--vc-slate))]">{t("form.consent")}</p>
    </form>
  );
};

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-semibold text-[hsl(var(--vc-ink))]">
      {label}{required && <span className="text-brand"> *</span>}
    </Label>
    {children}
  </div>
);

export default ContactForm;
